/**
 * aggregate-customer.service.ts — Customer 360 Sprint (Day 4)
 *
 * Façade over already-loaded data sources to produce the Customer 360
 * roll-ups shown inline on CustomerDetailModal's Info tab.
 *
 * Why a service:
 *   - One place to evolve "what counts as an open invoice" / "what counts as
 *     a signed proposal" / "what's in the audit feed" without spreading those
 *     definitions across the modal.
 *   - Promise.allSettled inside the orchestrator so one failed sub-derivation
 *     never blanks the whole Info tab (e.g. a future audit query failing
 *     mustn't take down the proposals card).
 *
 * Data residency:
 *   - Signed proposals + invoices are derived CLIENT-SIDE from `deals[]` —
 *     no new Firestore queries this sprint. CustomerDetailModal already
 *     fetches deals via its existing Promise.allSettled block at line ~393.
 *   - Audit entries land Day 5 — will query `customer_activity_log`. The
 *     service signature already accommodates it.
 */

import {
  addDoc,
  collection,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import type { Deal } from '@/types/theo-workflow.types';

// `Deal.proposalSignedAt` is typed `Timestamp` but at runtime can be either
// a Firestore Timestamp OR an ISO string (BP webhook writes via Admin SDK
// vs migrations writing strings). Helpers below normalise both shapes.
type SignedAtValue = string | Timestamp | undefined | null;

function signedAtMillis(value: SignedAtValue): number {
  if (!value) return 0;
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate().getTime();
  }
  const d = new Date(String(value));
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SignedProposalSummary {
  dealId: string;
  dealTitle: string;
  /** May be a Firestore Timestamp object OR an ISO string at runtime
   *  depending on the write path (BP webhook vs migration). Use
   *  signedAtMillis() before any arithmetic / formatting. */
  signedAt?: SignedAtValue;
  proposalUrl?: string;
  /** Number of distinct accepted line items on the BP-style accepted-items array. */
  acceptedItemCount: number;
  /** Total deal value (one-time, inc-GST). */
  oneTimeValue: number;
  /** Monthly recurring, if any. */
  monthlyRecurring: number;
}

/**
 * "Open invoice" = anything the customer owes or is about to owe. We mirror
 * the definition `billing-report.service.ts:187` uses (the source of truth
 * for the /home/billing screen) so the customer modal and the billing page
 * show the same numbers. That means:
 *   - issued and unpaid (billingStatus === 'billed')
 *   - flagged bad debtor (billingStatus === 'bad_debtor' — still unpaid)
 *   - awaiting invoice (billingStatus === 'to_bill' OR missing — at BILLING
 *     stage but invoice not yet sent)
 *   - excluding 'paid' and soft-deleted deals
 *
 * Audit-notes context: an earlier draft of this filter excluded `to_bill`,
 * which caused the customer modal's "outstanding total" to disagree with
 * the Billing page for the SAME customer. Aligned in the v1 fix-pass.
 */
export interface InvoiceSummary {
  /** Sum of `deal.value` across open invoices, in AUD. */
  outstandingTotal: number;
  /** Count of open invoices. */
  openCount: number;
  /** Count of invoices flagged bad debtor (subset of openCount). */
  badDebtorCount: number;
  /** Most recent invoice in the open set (for "last invoiced" display). */
  mostRecentBilledAt?: string;
}

/** Discrete event types written into the audit log. Free-form additions are
 *  fine — keep the existing values stable since older entries query against them. */
export type CustomerActivityEntryType =
  | 'stage_change'
  | 'note'
  | 'contact_made'
  | 'proposal_signed'
  | 'invoice_issued'
  | 'service_change';

export type CustomerActivitySourceEntity = 'deal' | 'job' | 'fault' | 'service' | 'call';

export interface CustomerActivityLogEntry {
  id?: string;
  tenantId: string;
  customerId: string;
  /** ISO timestamp. Used directly in the composite index. */
  timestamp: string;
  actorId: string;
  actorName: string;
  entryType: CustomerActivityEntryType;
  /** Short human-readable description. */
  summary: string;
  sourceEntity: CustomerActivitySourceEntity;
  /** ID of the source doc (deal id, fault id, etc.). */
  sourceId: string;
}

export interface CustomerAggregates {
  signedProposals: SignedProposalSummary[];
  invoiceSummary: InvoiceSummary;
  /** Newest-first list of audit entries from `customer_activity_log`. */
  auditEntries: CustomerActivityLogEntry[];
}

// ---------------------------------------------------------------------------
// Sync derivers (exported so consumers can call individually if needed)
// ---------------------------------------------------------------------------

export function deriveSignedProposals(deals: Deal[]): SignedProposalSummary[] {
  // Audit-notes context: an earlier draft used `proposalUrl` OR
  // `proposalAcceptedItems.length > 0`, which matched UNSIGNED drafts
  // because BP auto-creates `proposalUrl` on QUOTING entry. The only
  // truthy signal for "customer has actually signed" is
  // `proposalSignedAt` — matches the gate in ProposalStatusCard.tsx:62.
  return deals
    .filter((d) => !d.deleted)
    .filter((d) => Boolean(d.proposalSignedAt))
    .map<SignedProposalSummary>((d) => ({
      dealId: d.id ?? '',
      dealTitle: d.title || d.customerName || 'Untitled deal',
      signedAt: d.proposalSignedAt,
      proposalUrl: d.proposalUrl,
      acceptedItemCount: d.proposalAcceptedItems?.length ?? 0,
      oneTimeValue: Number(d.value ?? 0),
      monthlyRecurring: Number(d.monthlyRecurring ?? 0),
    }))
    // Newest first. signedAtMillis() handles both Firestore Timestamp and
    // ISO-string runtime shapes — direct localeCompare on a Timestamp
    // object returns nonsense.
    .sort((a, b) => signedAtMillis(b.signedAt) - signedAtMillis(a.signedAt));
}

export function deriveInvoiceSummary(deals: Deal[]): InvoiceSummary {
  // Open = anything the customer owes or is about to owe — mirrors the
  // /home/billing screen definition. billing-report.service.ts only ever
  // sees BILLING-stage deals (`where('stage', '==', 'BILLING')`); here we
  // receive ALL of a customer's deals, so we have to add that filter
  // ourselves. Otherwise an early-stage deal (INTAKE / PRESITE / QUOTING)
  // with no billingStatus would be mis-counted as "awaiting invoice".
  const open = deals.filter((d) => {
    if (d.deleted) return false;
    const isBillingStage = d.stage === 'BILLING';
    const status = d.billingStatus;
    if (!isBillingStage) {
      // Deal has moved past BILLING (typically to COMPLETED). Still counts
      // if invoice is sent-but-unpaid or flagged bad debtor.
      return status === 'billed' || status === 'bad_debtor';
    }
    // At BILLING stage — include unpaid + awaiting-invoice states.
    return status !== 'paid';
  });

  const outstandingTotal = open.reduce((sum, d) => sum + Number(d.value ?? 0), 0);
  const badDebtorCount = open.filter((d) => d.billingStatus === 'bad_debtor').length;

  // Most recent billedAt across the open set.
  let mostRecentBilledAt: string | undefined;
  for (const d of open) {
    if (d.billedAt && (!mostRecentBilledAt || d.billedAt > mostRecentBilledAt)) {
      mostRecentBilledAt = d.billedAt;
    }
  }

  return {
    outstandingTotal,
    openCount: open.length,
    badDebtorCount,
    mostRecentBilledAt,
  };
}

// ---------------------------------------------------------------------------
// Audit log — write + read
// ---------------------------------------------------------------------------

const AUDIT_FETCH_LIMIT = 100;
const DEDUP_WINDOW_MS = 60_000;
const DEDUP_SWEEP_INTERVAL_MS = 5 * 60_000;

/**
 * Best-effort human name for an audit actor. Some upstream services (fault
 * stage transitions, customer-service activate/suspend/cancel) only thread
 * a userId through their signatures — they can't supply a name. When that
 * userId matches the currently-logged-in user (the typical case for
 * client-initiated writes), we substitute `logged_in_user` from
 * localStorage so the lifecycle feed shows "Casey" instead of "user-abc123".
 */
export function resolveActorName(actorId: string, providedName?: string): string {
  if (providedName && providedName !== actorId) return providedName;
  const sessionUserId =
    localStorage.getItem('user_id') || localStorage.getItem('tech_id') || '';
  const sessionName = localStorage.getItem('logged_in_user') || '';
  if (sessionName && sessionUserId === actorId) return sessionName;
  return providedName || actorId;
}

/**
 * In-memory dedup cache. Keys are `${sourceId}|${entryType}|${actorId}|${summary}`,
 * values are the Unix-ms timestamp of the LAST successful write under that
 * key. Drops entries older than DEDUP_WINDOW_MS on each access via a lazy
 * sweep — Map can otherwise grow unbounded over the life of the tab.
 *
 * The dedup is best-effort (single tab, single session). Two tabs writing
 * the same event within 60s can still produce duplicates — virtual-merge
 * suppression on the read side (see CustomerDetailModal Lifecycle filter)
 * is the secondary defence.
 */
const dedupCache = new Map<string, number>();
let lastSweepAt = 0;

function dedupKey(entry: Pick<CustomerActivityLogEntry, 'sourceId' | 'entryType' | 'actorId' | 'summary'>): string {
  return `${entry.sourceId}|${entry.entryType}|${entry.actorId}|${entry.summary}`;
}

function sweepDedupCache(now: number) {
  if (now - lastSweepAt < DEDUP_SWEEP_INTERVAL_MS) return;
  lastSweepAt = now;
  for (const [key, ts] of dedupCache) {
    if (now - ts > DEDUP_WINDOW_MS) dedupCache.delete(key);
  }
}

/**
 * Write an audit entry to `customer_activity_log`. Fire-and-forget caller
 * pattern — invoke as `logCustomerActivity(...).catch(console.error)` from
 * the forward-write hooks so a logging failure never rolls back the
 * surrounding business write.
 *
 * Dedup window: 60 seconds keyed on (sourceId, entryType, actorId, summary).
 * Deal stage saves can fire many times in quick succession during scripted
 * advance flows — without dedup the audit feed would gain doubles.
 *
 * `tenantId` defaults to the canonical session tenant via `getTenantIdSync()`.
 * If the caller already has it (e.g. from useTenant) they can pass it in.
 */
export async function logCustomerActivity(
  entry: Omit<CustomerActivityLogEntry, 'id' | 'tenantId' | 'timestamp'> & {
    tenantId?: string;
    timestamp?: string;
  },
): Promise<void> {
  if (!entry.customerId || !entry.sourceId) {
    console.warn('[logCustomerActivity] missing customerId or sourceId; skipping');
    return;
  }

  const tenantId = entry.tenantId ?? getTenantIdSync();
  const timestamp = entry.timestamp ?? new Date().toISOString();
  const now = Date.now();
  sweepDedupCache(now);

  const key = dedupKey(entry);
  const lastWrite = dedupCache.get(key);
  if (lastWrite && now - lastWrite < DEDUP_WINDOW_MS) {
    // Duplicate inside the window — skip the write silently. This is the
    // happy-path defence; double-fires from scripted flows (e.g.
    // moveDealToStage running its own activityLog write + this audit
    // entry on the same transition) are common.
    return;
  }

  const payload: CustomerActivityLogEntry = {
    tenantId,
    customerId: entry.customerId,
    timestamp,
    actorId: entry.actorId || '',
    actorName: entry.actorName || '',
    entryType: entry.entryType,
    summary: entry.summary,
    sourceEntity: entry.sourceEntity,
    sourceId: entry.sourceId,
  };

  await addDoc(collection(db, 'customer_activity_log'), payload);
  dedupCache.set(key, now);
}

/**
 * Fetch newest-first audit entries for a customer. Uses tenantIdReadUnion()
 * because `customer_activity_log` is a strict-rule collection — list
 * queries WITHOUT a `tenantId` filter are rejected by Firestore rules even
 * when every doc would belong to the user's tenant (CLAUDE.md Critical
 * Rule #9). Composite index declared in `firestore.indexes.json`.
 */
export async function getCustomerActivityLog(
  customerId: string,
): Promise<CustomerActivityLogEntry[]> {
  if (!customerId) return [];
  const tenantUnion = tenantIdReadUnion(getTenantIdSync());
  const q = query(
    collection(db, 'customer_activity_log'),
    where('tenantId', 'in', tenantUnion),
    where('customerId', '==', customerId),
    orderBy('timestamp', 'desc'),
    fbLimit(AUDIT_FETCH_LIMIT),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CustomerActivityLogEntry, 'id'>) }));
}

// ---------------------------------------------------------------------------
// Orchestrator — Promise.allSettled so one failure never blanks the panel
// ---------------------------------------------------------------------------

interface AggregateInput {
  customerId: string;
  /** Already-loaded deals[] from the modal's primary fetch. */
  deals: Deal[];
}

export async function getCustomerAggregates({
  customerId,
  deals,
}: AggregateInput): Promise<CustomerAggregates> {
  // Each lane wrapped in a promise so allSettled can isolate failures.
  // The audit fetch is genuinely async (Firestore query against
  // customer_activity_log); if it fails the other two lanes still
  // produce values.
  const [proposalsResult, invoicesResult, auditResult] = await Promise.allSettled([
    Promise.resolve(deriveSignedProposals(deals)),
    Promise.resolve(deriveInvoiceSummary(deals)),
    getCustomerActivityLog(customerId),
  ]);

  const signedProposals =
    proposalsResult.status === 'fulfilled' ? proposalsResult.value : [];
  if (proposalsResult.status === 'rejected') {
    console.error('[aggregate-customer] signed proposals derivation failed:', proposalsResult.reason);
  }

  const invoiceSummary: InvoiceSummary =
    invoicesResult.status === 'fulfilled'
      ? invoicesResult.value
      : { outstandingTotal: 0, openCount: 0, badDebtorCount: 0 };
  if (invoicesResult.status === 'rejected') {
    console.error('[aggregate-customer] invoice summary derivation failed:', invoicesResult.reason);
  }

  const auditEntries: CustomerActivityLogEntry[] =
    auditResult.status === 'fulfilled' ? auditResult.value : [];
  if (auditResult.status === 'rejected') {
    console.error('[aggregate-customer] audit fetch failed:', auditResult.reason);
  }

  return {
    signedProposals,
    invoiceSummary,
    auditEntries,
  };
}
